import { useState, useCallback } from 'react';
import ToolLayout from '../../components/layout/ToolLayout';
import { Button, Card, CardContent, CopyButton, DownloadButton, Select } from '@devkit/ui';
import { Braces } from 'lucide-react';
import { trackToolExecute, trackCopy, trackDownload, trackClear } from '@devkit/analytics';

const indentOptions = [
  { value: '2', label: '2 spaces' },
  { value: '4', label: '4 spaces' },
  { value: 'tab', label: 'Tab' },
];

const defaultJson = `{
  "name": "DevKit",
  "description": "Fast, private developer tools",
  "features": [
    "JSON Formatter",
    "JWT Decoder",
    "Base64 Encoder"
  ],
  "stats": {
    "users": 10000,
    "tools": 30
  }
}`;

export default function JsonFormatterPage() {
  const [input, setInput] = useState(defaultJson);
  const [output, setOutput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [indent, setIndent] = useState('2');

  const formatJson = useCallback(() => {
    setError(null);
    try {
      const parsed = JSON.parse(input);
      const indentSize = indent === 'tab' ? '\t' : parseInt(indent);
      const formatted = JSON.stringify(parsed, null, indentSize);
      setOutput(formatted);
      trackToolExecute('json-formatter', 'format');
    } catch (e) {
      setError((e as Error).message);
      setOutput('');
    }
  }, [input, indent]);

  const minifyJson = useCallback(() => {
    setError(null);
    try {
      const parsed = JSON.parse(input);
      setOutput(JSON.stringify(parsed));
      trackToolExecute('json-formatter', 'minify');
    } catch (e) {
      setError((e as Error).message);
      setOutput('');
    }
  }, [input]);

  const validateJson = useCallback(() => {
    setError(null);
    try {
      JSON.parse(input);
      setOutput('✅ Valid JSON');
      trackToolExecute('json-formatter', 'validate');
    } catch (e) {
      setError((e as Error).message);
      setOutput('');
    }
  }, [input]);

  const clearAll = useCallback(() => {
    setInput('');
    setOutput('');
    setError(null);
    trackClear('json-formatter');
  }, []);

  return (
    <ToolLayout slug="json-formatter">
      <Card>
        <CardContent className="p-6">
          {/* Options */}
          <div className="flex items-center gap-4 mb-4 pb-4 border-b border-slate-700">
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-400">Indent:</label>
              <Select
                options={indentOptions}
                value={indent}
                onChange={setIndent}
                className="w-32"
              />
            </div>
          </div>

          {/* Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Input JSON
            </label>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Paste your JSON here..."
              className="w-full h-48 px-4 py-3 bg-surface border border-slate-700 rounded-lg font-mono text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary resize-y"
            />
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2 mb-4">
            <Button onClick={formatJson} className="gap-2">
              <Braces className="w-4 h-4" />
              Format
            </Button>
            <Button variant="secondary" onClick={minifyJson} className="gap-2">
              Minify
            </Button>
            <Button variant="outline" onClick={validateJson} className="gap-2">
              Validate
            </Button>
            <Button variant="ghost" onClick={clearAll} className="gap-2">
              Clear
            </Button>
          </div>

          {/* Output */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-300">
                Output
              </label>
              {output && (
                <div className="flex items-center gap-2">
                  <CopyButton
                    text={output}
                    onCopy={() => trackCopy('json-formatter')}
                  />
                  <DownloadButton
                    content={output}
                    filename="formatted.json"
                    onDownload={() => trackDownload('json-formatter')}
                  />
                </div>
              )}
            </div>
            <textarea
              value={error ? '' : output}
              readOnly
              placeholder={error ? '' : 'Output will appear here...'}
              className={`w-full h-48 px-4 py-3 bg-surface border rounded-lg font-mono text-sm text-slate-100 resize-y ${
                error ? 'border-red-500' : 'border-slate-700'
              }`}
            />
            {error && (
              <div className="mt-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-sm text-red-400 font-medium">Error</p>
                <p className="text-sm text-red-300 mt-1">{error}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </ToolLayout>
  );
}